import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu } from "lucide-react";
import { motion } from "framer-motion";
import { Crest } from "@/components/Crest";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const LINKS = [
  { to: "/", label: "Αρχική" },
  { to: "/roster", label: "Ρόστερ" },
  { to: "/partners", label: "Συνεργάτες" },
  { to: "/contact", label: "Επικοινωνία" },
];

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkClass = ({ isActive }) =>
    `relative font-display text-lg font-600 uppercase tracking-wide transition-colors duration-200 ${
      isActive ? "text-gold" : "text-ice hover:text-white"
    }`;

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className={`fixed inset-x-0 top-0 z-50 border-b transition-all duration-300 ${
        scrolled
          ? "border-white/10 bg-navy/90 backdrop-blur-xl"
          : "border-transparent bg-gradient-to-b from-navy/80 to-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="club-container flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center gap-3 py-3"
          data-testid="nav-logo-link"
        >
          <Crest size={scrolled ? 42 : 52} className="transition-all duration-300" />
          <div className="leading-none">
            <span className="font-heading block text-lg font-800 tracking-tight text-white sm:text-xl">
              Ρεάλ
            </span>
            <span className="font-display block text-xs font-600 uppercase tracking-[0.2em] text-gold">
              Δυτικής Αττικής
            </span>
          </div>
        </Link>

        <nav className="hidden items-center gap-9 md:flex">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={linkClass}
              data-testid={`nav-link-${l.label}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button
              className="rounded-md p-2 text-ice md:hidden"
              aria-label="Μενού"
              data-testid="nav-hamburger"
            >
              <Menu size={28} />
            </button>
          </SheetTrigger>
          <SheetContent
            side="right"
            className="border-l border-white/10 bg-navy text-ice"
          >
            <div className="mt-10 flex flex-col gap-6">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `font-display text-2xl font-700 uppercase tracking-wide ${
                      isActive ? "text-gold" : "text-ice"
                    }`
                  }
                  data-testid={`nav-mobile-link-${l.label}`}
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </motion.header>
  );
}
